import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import {
  Grid,
  InputLabel,
  Box,
  Select,
  MenuItem,
  FormControl,
  FormLabel,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Typography,
} from '@mui/material';
import { Field, Formik, type FormikHelpers } from 'formik';
import { ChevronRight, ChevronLeft } from '@mui/icons-material';
import { COLORS } from '../../../../utils/colors';
import {
  educations,
  employeeStatus,
  emptyOtherDetails,
  ethnics,
  genders,
  highestDL,
  interestedInOptions,
  IOtherDetails,
  publicAssistance as publicAssistanceData,
  referralSource,
  ReferralSourceConst,
} from '../utils';

// IDs of Household Supports & Income options that surface a Case Number field:
// Cash Assistance Receipt (1), SNAP Receipt (2), Medicaid Receipt (3).
// NYCHA residency (4) does not issue case numbers, so it is intentionally
// excluded. The Case Number itself is always optional regardless of selection.
const CASE_NUMBER_OPTION_IDS = [1, 2, 3];

const OtherInfo = (props: any) => {
  const [selectedEthnic, setSelectedEthnic] = useState<string>('');
  const [selectedReferralSource, setSelectedReferralSource] = useState<string>('');
  const [selectedPublicAssistance, setSelectedPublicAssistance] = useState<string[]>(
    props.data?.publicAssistance || []
  );
  const [selectedInterestedInFields, setSelectedInterestedInFields] = React.useState<string[]>(
    props.data?.interestedIn || []
  );

  const [isHSISelected, setIsHSISelected] = useState(props.data?.publicAssistance.length > 0 ? true : false);

  // Sync local state with props.data when coming back from previous step
  useEffect(() => {
    if (props.isBackMode && props.data) {
      setSelectedEthnic(props.data.ethnic || '');
      setSelectedReferralSource(props.data.referralSource || '');
      setSelectedPublicAssistance(props.data.publicAssistance || []);
      setSelectedInterestedInFields(props.data.interestedIn || []);
    }
  }, [props.isBackMode, props.data]);

  // Create dynamic schema based on selected public assistance
  const getSchema = () => {
    return yup.object().shape({
      gender: yup.string().required('Gender is required'),
      otherGender: yup.string().when('gender', {
        is: (val: string) => val === 'Other',
        then: () => yup.string().required('Please specify other gender'),
        otherwise: () => yup.string().optional(),
      }),
      ethnic: yup.string(),
      employmentStatus: yup.string(),
      educationalBackground: yup.string().required('Educational Background is required'),
      caseNumber: yup.string().optional(),
      publicAssistance: yup.array().required('Public Assistance is required'),
      publicAssistanceOther: yup.string(),
      ethnicOther: yup.string().when('ethnic', {
        is: (val: string) => val === 'Other, Specify',
        then: () => yup.string().required('Please specify other ethnicity'),
        otherwise: () => yup.string().optional(),
      }),
      referralSource: yup.string().required('Referral source is required'),
      refCommunityOrg: yup
        .string()
        .when('referralSource', ([referralSource], schema) =>
          referralSource === ReferralSourceConst.COMMUNITY_BASED_ORG
            ? schema.required('Community organization is required')
            : schema.notRequired()
        ),
      highestDL: yup.string().required('DL Info required'),
    });
  };

  const getValuesOnBack = () => {
    return {
      gender: props.data?.gender || '',
      otherGender: props.data?.otherGender || '',
      ethnic: props.data?.ethnic || '',
      employmentStatus: props.data?.employmentStatus || '',
      educationalBackground: props.data?.educationalBackground || '',
      caseNumber: props.data?.caseNumber || '',
      publicAssistance: props.data?.publicAssistance || [],
      publicAssistanceOther: props.data?.publicAssistanceOther || '',
      ethnicOther: props.data?.ethnicOther || '',
      referralSource: props.data?.referralSource || '',
      refCommunityOrg: props.data?.refCommunityOrg || '',
      interestedIn: props.data?.interestedIn || [],
      highestDL: props.data?.highestDL || '',
    };
  };

  const saveInfo = (values: IOtherDetails, { setSubmitting }: FormikHelpers<IOtherDetails>) => {
    const payload = {
      ...values,
      publicAssistance: selectedPublicAssistance,
      interestedIn: selectedInterestedInFields,
    };
    props.handleNext(payload);
    setSelectedPublicAssistance([]);
    setSubmitting(false);
  };

  const handleSelect = (item: string, formikProps: any) => {
    // Define the option groups
    // "Specific options" = options 1-6 (mutually exclusive with 7 and 8 at the selection level)
    const specificOptions = publicAssistanceData
      .filter((option) => option.id !== 7 && option.id !== 8)
      .map((option) => option.title);
    const option7 = publicAssistanceData.find((option) => option.id === 7)?.title;
    const option8 = publicAssistanceData.find((option) => option.id === 8)?.title;

    // Titles of options that surface the Case Number field (1, 2, 3)
    const caseNumberTitles = publicAssistanceData
      .filter((option) => CASE_NUMBER_OPTION_IDS.includes(option.id))
      .map((option) => option.title);

    let newSelection: string[] = [];

    if (selectedPublicAssistance?.includes(item)) {
      // If item is already selected, just remove it
      newSelection = selectedPublicAssistance?.filter((id: string) => id !== item);
    } else {
      // If item is being selected
      if (specificOptions.includes(item)) {
        // If selecting a specific option (1-6), remove options 7 and 8
        const filteredSelection = selectedPublicAssistance?.filter((id: string) => id !== option7 && id !== option8);
        newSelection = [...filteredSelection, item];
      } else if (item === option7) {
        // If selecting option 7, remove all specific options (1-6) and option 8
        const filteredSelection = selectedPublicAssistance?.filter(
          (id: string) => !specificOptions.includes(id) && id !== option8
        );
        newSelection = [...filteredSelection, item];
      } else if (item === option8) {
        // If selecting option 8, remove all specific options (1-6) and option 7
        const filteredSelection = selectedPublicAssistance?.filter(
          (id: string) => !specificOptions.includes(id) && id !== option7
        );
        newSelection = [...filteredSelection, item];
      } else {
        // For any other case, just add the item
        newSelection = [...selectedPublicAssistance, item];
      }
    }

    // Clear the case number field when no remaining selection surfaces it.
    // This covers: picking option 4-8 alone, or deselecting the last 1-3 option.
    const caseNumberStillShowing = newSelection.some((sel: string) => caseNumberTitles.includes(sel));
    if (!caseNumberStillShowing) {
      formikProps.setFieldValue('caseNumber', '');
      formikProps.setFieldError('caseNumber', undefined);
    }

    // Update local state
    setSelectedPublicAssistance(newSelection);

    // Update Formik values in real-time
    formikProps.setFieldValue('publicAssistance', newSelection);
  };

  const handleSelectForInterestedIn = (item: string, formikProps: any) => {
    let newSelection: string[] = [];

    if (selectedInterestedInFields?.includes(item)) {
      newSelection = selectedInterestedInFields?.filter((id: string) => id !== item);
    } else {
      newSelection = [...selectedInterestedInFields, item];
    }

    // Update local state
    setSelectedInterestedInFields(newSelection);

    // Update Formik values in real-time
    formikProps.setFieldValue('interestedIn', newSelection);
  };

  useEffect(() => {
    if (selectedPublicAssistance.length > 0) {
      setIsHSISelected(true);
    } else {
      setIsHSISelected(false);
    }
  }, [selectedPublicAssistance]);

  return (
    <Box sx={localStyle.mainBoxStyle}>
      <Formik
        initialValues={props.isBackMode ? getValuesOnBack() : emptyOtherDetails}
        onSubmit={saveInfo}
        validationSchema={getSchema()}
        validateOnMount={true}>
        {({
          values,
          handleSubmit,
          handleChange,
          handleBlur,
          touched,
          errors,
          isValid,
          setFieldValue,
          setFieldError,
        }) => {
          return (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} lg={4}>
                  <FormControl fullWidth>
                    <InputLabel sx={localStyle.formLabel} id='gender-label' required>
                      Gender
                    </InputLabel>
                    <Field
                      as={Select}
                      labelId='gender-label'
                      label='Gender'
                      name='gender'
                      value={values.gender || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.gender && Boolean(errors.gender)}>
                      {genders.map((option) => (
                        <MenuItem key={option.id} value={option.title}>
                          {option.title}
                        </MenuItem>
                      ))}
                    </Field>
                    {touched.gender && errors.gender ? (
                      <Typography color='error' variant='caption'>
                        {errors.gender as string}
                      </Typography>
                    ) : null}
                  </FormControl>
                </Grid>
                {values.gender === 'Other' ? (
                  <Grid item xs={12} lg={4}>
                    <TextField
                      label='Please specify'
                      fullWidth
                      placeholder='Please specify other gender'
                      name='otherGender'
                      value={values.otherGender || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.otherGender && Boolean(errors.otherGender)}
                      helperText={touched.otherGender && (errors.otherGender as string)}
                    />
                  </Grid>
                ) : null}

                <Grid item xs={12} lg={4}>
                  <FormControl fullWidth>
                    <InputLabel style={{ color: COLORS.palette.black }} id='ethnic-label'>
                      Race/Ethnic Identity
                    </InputLabel>
                    <Field
                      as={Select}
                      labelId='ethnic-label'
                      name='ethnic'
                      label='Race/Ethnic Identity'
                      value={values.ethnic || ''}
                      onChange={(event: any) => {
                        handleChange(event);
                        setSelectedEthnic(event.target.value);
                      }}
                      onBlur={handleBlur}
                      error={touched.ethnic && Boolean(errors.ethnic)}
                      helperText={touched.ethnic && errors.ethnic}>
                      {ethnics.map((option) => (
                        <MenuItem key={option.id} value={option.title}>
                          {option.title}
                        </MenuItem>
                      ))}
                    </Field>
                  </FormControl>
                </Grid>

                {selectedEthnic === ethnics.find((i) => i.id === 8)?.title ? (
                  <Grid item xs={12} lg={4}>
                    <TextField
                      label='Please specify'
                      fullWidth
                      placeholder='Please specify other Ethnic Identity'
                      name='ethnicOther'
                      value={values.ethnicOther || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.ethnicOther && Boolean(errors.ethnicOther)}
                      helperText={touched.ethnicOther && errors.ethnicOther ? (errors.ethnicOther as string) : ''}
                    />
                  </Grid>
                ) : null}

                <Grid item xs={12} lg={4}>
                  <FormControl fullWidth>
                    <InputLabel style={{ color: COLORS.palette.black }} id='employmentStatus-label'>
                      Employment Status
                    </InputLabel>
                    <Field
                      as={Select}
                      labelId='employmentStatus-label'
                      name='employmentStatus'
                      label='Employment Status'
                      value={values.employmentStatus || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.employmentStatus && Boolean(errors.employmentStatus)}>
                      {employeeStatus.map((option) => (
                        <MenuItem key={option.id} value={option.title}>
                          {option.title}
                        </MenuItem>
                      ))}
                    </Field>
                  </FormControl>
                </Grid>

                <Grid item xs={12} lg={4}>
                  <FormControl fullWidth>
                    <InputLabel sx={localStyle.formLabel} required>
                      Highest Driver’s License Obtained
                    </InputLabel>
                    <Field
                      as={Select}
                      name='highestDL'
                      label="Highest Driver's license obtained"
                      value={values.highestDL || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.highestDL && Boolean(errors.highestDL)}>
                      {highestDL.map((option) => (
                        <MenuItem key={option.id} value={option.title}>
                          {option.title}
                        </MenuItem>
                      ))}
                    </Field>
                    {touched.highestDL && errors.highestDL ? (
                      <Typography color='error' variant='caption'>
                        {errors.highestDL as string}
                      </Typography>
                    ) : null}
                  </FormControl>
                </Grid>

                <Grid item xs={12} lg={4}>
                  <FormControl fullWidth>
                    <InputLabel sx={localStyle.formLabel} id='education-label' required>
                      Educational Background
                    </InputLabel>
                    <Field
                      as={Select}
                      labelId='education-label'
                      name='educationalBackground'
                      label='Educational Background'
                      value={values.educationalBackground || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.educationalBackground && Boolean(errors.educationalBackground)}>
                      {educations.map((option) => (
                        <MenuItem key={option.id} value={option.title}>
                          {option.title}
                        </MenuItem>
                      ))}
                    </Field>
                    {touched.educationalBackground && errors.educationalBackground ? (
                      <Typography color='error' variant='caption'>
                        {errors.educationalBackground as string}
                      </Typography>
                    ) : null}
                  </FormControl>
                </Grid>

                <Grid item xs={12} lg={4}>
                  <FormControl fullWidth>
                    <InputLabel sx={localStyle.formLabel} id='referral-source' required>
                      Referral Source
                    </InputLabel>
                    <Field
                      as={Select}
                      labelId='referral-source'
                      name='referralSource'
                      label='Referral Source'
                      value={values.referralSource || ''}
                      onChange={(event: any) => {
                        handleChange(event);
                        setSelectedReferralSource(event.target.value);
                      }}
                      onBlur={handleBlur}
                      error={touched.referralSource && Boolean(errors.referralSource)}>
                      {referralSource.map((option) => (
                        <MenuItem key={option.id} value={option.title}>
                          {option.title}
                        </MenuItem>
                      ))}
                    </Field>
                    {touched.referralSource && errors.referralSource ? (
                      <Typography color='error' variant='caption'>
                        {errors.referralSource as string}
                      </Typography>
                    ) : null}
                  </FormControl>
                </Grid>

                {selectedReferralSource ===
                referralSource.find((i) => i.id === ReferralSourceConst.COMMUNITY_BASED_ORG_ID)?.title ? (
                  <Grid item xs={12} lg={4}>
                    <TextField
                      label='Please specify'
                      fullWidth
                      placeholder='Please specify other Referral Source'
                      name='refCommunityOrg'
                      value={values.refCommunityOrg || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.refCommunityOrg && Boolean(errors.refCommunityOrg)}
                      helperText={
                        touched.refCommunityOrg && errors.refCommunityOrg ? (errors.refCommunityOrg as string) : ''
                      }
                    />
                  </Grid>
                ) : null}

                <Grid item xs={12} lg={4}>
                  <FormControl>
                    <FormLabel sx={{ fontSize: 17, fontWeight: '600' }} id='demo-row-radio-buttons-group-label'>
                      Interested In
                    </FormLabel>
                    <List>
                      {interestedInOptions.map((item: any) => (
                        <ListItem key={item.id} disablePadding>
                          <Checkbox
                            size='small'
                            onChange={() => {
                              handleSelectForInterestedIn(item.title, { setFieldValue, setFieldError });
                            }}
                            checked={selectedInterestedInFields.includes(item.title)}
                            sx={{ alignSelf: 'flex-start' }}
                          />
                          <ListItemText primary={item.title} />
                        </ListItem>
                      ))}
                    </List>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4} lg={4}>
                  <FormControl>
                    <FormLabel
                      sx={{
                        fontSize: 17,
                        fontWeight: '600',
                        marginBottom: '-10px',
                        marginTop: '-10px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      id='demo-row-radio-buttons-group-label'>
                      Household Supports and Income
                      <Typography component='span' sx={{ color: 'red', marginLeft: 0.5, fontSize: '2em' }}>
                        *
                      </Typography>
                    </FormLabel>
                    <Typography variant='caption' sx={{ fontSize: 14, color: 'text.secondary', display: 'block' }}>
                      PINCC services are targeted to New Yorkers who fall within the below categories. Check all that
                      apply:
                    </Typography>
                    <List>
                      {publicAssistanceData.map((item: any) => (
                        <ListItem key={item.id} disablePadding>
                          <Checkbox
                            size='small'
                            onChange={() => {
                              handleSelect(item.title, { setFieldValue, setFieldError });
                            }}
                            checked={selectedPublicAssistance.includes(item.title)}
                            sx={{ alignSelf: 'flex-start' }}
                          />
                          <ListItemText primary={item.title} />
                        </ListItem>
                      ))}
                    </List>
                    {touched.publicAssistance && errors.publicAssistance && (
                      <Typography color='error' variant='caption'>
                        {errors.publicAssistance as string}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                {(() => {
                  const caseNumberRequiringOptions = publicAssistanceData
                    .filter((option) => CASE_NUMBER_OPTION_IDS.includes(option.id))
                    .map((option) => option.title);
                  const hasSpecificOptions = selectedPublicAssistance?.some((item: string) =>
                    caseNumberRequiringOptions.includes(item)
                  );

                  return hasSpecificOptions ? (
                    <Grid item xs={12} lg={4}>
                      <TextField
                        label='Case Number'
                        fullWidth
                        placeholder='Case Number'
                        name='caseNumber'
                        value={values.caseNumber || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.caseNumber && Boolean(errors.caseNumber)}
                        helperText={touched.caseNumber && errors.caseNumber ? (errors.caseNumber as string) : ''}
                      />
                    </Grid>
                  ) : null;
                })()}
              </Grid>

              <Box sx={localStyle.buttonBoxStyle}>
                <Button
                  color='inherit'
                  disabled={false}
                  onClick={() => {
                    const payload = {
                      ...values,
                      publicAssistance: selectedPublicAssistance,
                      interestedIn: selectedInterestedInFields,
                    };
                    props.handleBack(payload);
                    setSelectedPublicAssistance([]);
                    setSelectedInterestedInFields([]);
                  }}
                  sx={{ mr: 1 }}
                  startIcon={<ChevronLeft />}
                  size={'large'}>
                  Back
                </Button>
                <Box sx={{ flex: '1 1 auto' }} />
                <Button
                  type='submit'
                  variant='contained'
                  // @ts-ignore
                  color='darkModePrimary'
                  size='large'
                  disabled={isHSISelected && isValid ? false : true}
                  endIcon={<ChevronRight />}>
                  {'Next'}
                </Button>
              </Box>
            </form>
          );
        }}
      </Formik>
    </Box>
  );
};

const localStyle = {
  mainBoxStyle: {
    width: '100%',
    mt: 5,
  },

  buttonBoxStyle: {
    display: 'flex',
    flexDirection: 'row',
    mt: 5,
  },

  formLabel: {
    color: COLORS.palette.black,
    marginBottom: '5px',
    display: 'flex',
    alignItems: 'center',
    '& .MuiInputLabel-asterisk': {
      color: 'red',
      fontSize: '2em',
      marginLeft: '-3px',
    },
  },
};

export default OtherInfo;
